import { AxiosResponse } from 'axios';

import Local from '../classes/local';
import { ApiError } from '../functions/getApiErrorMessage';
import getUrlParameters from '../functions/getUrlParameters';
import useAxios from './useAxios';
import getClientId from '../functions/getClientId';
import { LOGIN_QUERY_STRING_KEY } from '../../login/components/Login';
import { SecurityCodeMethod } from '../../mfa/hooks/useMfaApi';
import { loginSuccessEvent } from '../analytics';
import { redirectTo } from '../functions/redirectTo';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { mockGovDeliveryUrlFailParams } from '../functions/govDeliveryErrors';

export type UpdateField = 'username' | 'password' | 'question' | 'email';
export type SlsLoginErr =
    'SSO-0' |
    'SSO-1' |
    'SSO-2' |
    'SSO-3' |
    'SSO-4' |
    'SSO-5' |
    'SSO-6' |
    'SSO-7' |
    'SSO-8' |
    'SSO-9' |
    'SSO-10' |
    'SSO-11' |
    'SSO-12' |
    'SSO-13' |
    'MFA_AUTH_REQUIRED' |
    'MFA_EXPIRED_OTP' |
    'MFA_INVALID_OTP' |
    'MFA_SERVER_ERROR';

export type MfaSendErr =
    'MFA_GOVDELIVERY_UNAVAILABLE' |
    'MFA_USER_OPTED_OUT' |
    'MFA_METHOD_ID_INVALID';

export interface MfaOption {
    id: string
    type: SecurityCodeMethod
    mfa_channel: string
    deactivated?: boolean
    opted_out?: boolean
}
export interface MfaInfo {
    prompt_for_mfa_enrollment: boolean
    configured_options: MfaOption[]
}

export interface LoginRequestBody {
    username: string
    password: string
    client_id: string
    relay: string
    redirect_uri: string
    otp?: string
}

export interface LoginResponse {
    code?: SlsLoginErr
    location?: string // where to redirect user on successful login
    msg?: string
    mustUpdate?: UpdateField[]
    mfa_info?: MfaInfo
}
export interface MfaSendCodeRequest {
    id: string
}

export interface MfaSendCodeResponse {
    status: string
    code: number
    errors?: ApiError[]
}

export default function useLoginApi() {
    const axios = useAxios();
    const { showMaxAccountSettingsPage } = useFlags();
    const login = (username: string, password: string, otp?: string) => {
        const clientId = getClientId(showMaxAccountSettingsPage);
        const params = getUrlParameters();

        const redirectUri = params.get('redirect_uri') || '';
        let newRelayValue = params.get('relay') || '';
        if (!newRelayValue && (clientId.startsWith('max') ||  clientId.startsWith('mbp'))) {
            newRelayValue = Local.getItem('language', 'English') === 'English' ? 'en-us' : 'es-mx';
        }

        const requestBody: LoginRequestBody = {
            username,
            password,
            client_id: clientId,
            relay: newRelayValue,
            redirect_uri: redirectUri
        };
        if (otp) {
            requestBody.otp = otp;
        }

        return axios.call<LoginResponse>('POST', '/sso/signin', requestBody, true);
    }

    /** To be called after login API call succeeds. Cleans up saved query params and handles redirect */
    const loginSuccess = (loginResponse: AxiosResponse<LoginResponse>, loginType: 'mfa' | 'non-mfa') => {
        sessionStorage.removeItem(LOGIN_QUERY_STRING_KEY);
        loginSuccessEvent(loginType === 'mfa');
        if (loginResponse.data.location) {
            const redirectUrl = loginResponse.data.location;
            redirectTo(redirectUrl, 'Logged in successfully.');
        }
    }

    /** Sent an MFA code to the given MFA channel */
    const mfaSend = (id: string) => {
        const lang = Local.getItem('language') === 'Spanish' ? 'es' : 'en';
        const params = mockGovDeliveryUrlFailParams();
        const url = `/account/api/mfa/send${params}`;
        return axios.call<MfaSendCodeResponse>('POST', url, { id, lang, });
    }

    return {
        login,
        loginSuccess,
        mfaSend
    };
}
